import { Fragment, useCallback, useEffect, useState } from 'react';
import { authFetch } from '../../../services/api';
import { Carregando, EstadoVazio, Selo } from '../../common/interface';
import type { Papel } from '../../common/interface';
import { GavetaFeedback, type EstadoDaCaptura } from '../../feedback/GavetaFeedback';
import { capturarTela } from '../../feedback/captura';
import type { Tipo } from '../../feedback/api';
import { dataDoDia, moeda } from '../formato';

interface Pendencia { codigo: string; frase: string }
interface Nota {
  idNota: number; situacao: string; teste: boolean; valor: number;
  numeroNfse: string | null; chaveAcesso: string | null; autorizadaEm: string | null;
}
interface NotaDoMes {
  competencia: string; situacao: string; situacaoRotulo: string; valor: number; origemValor: 'apuracao' | 'previa';
  prontidao: Pendencia[]; nota: Nota | null; liberadoSemNota: { em: string } | null; acoes: string[];
}
interface NotaAnterior extends Nota { competencia: string; situacaoRotulo: string }
interface Ficha {
  cnpj: string; razaoSocial: string | null; inscricaoMunicipal: string | null; codMunicipio: string | null;
  regime: 'mei' | 'me_epp' | 'nao_optante' | null;
  certificado: { validoAte: string; situacao: 'ativo' | 'vencido'; venceEmDias: number } | null;
}

const REGIME: Record<string, string> = {
  mei: 'Simples Nacional · MEI', me_epp: 'Simples Nacional · ME/EPP', nao_optante: 'Não optante pelo Simples',
};

const PAPEL: Record<string, Papel> = {
  autorizada: 'sucesso', pronta: 'info', com_pendencia: 'atencao', rejeitada: 'perigo', sem_resposta: 'atencao',
  cancelamento_pedido: 'atencao', analise_fiscal: 'atencao',
};

function cnpjFormatado(c: string): string {
  return c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

async function ler<T>(caminho: string): Promise<T> {
  const res = await authFetch(caminho);
  const corpo = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((corpo && corpo.error) || 'Não foi possível ler a nota fiscal');
  return corpo.data as T;
}

/**
 * Aba Nota fiscal — spec 0113. Só leitura (RN-14).
 *
 * A NFS-e do serviço que o cooperado PJ presta à cooperativa é emitida pela
 * cooperativa, com o certificado dele. Aqui ele acompanha a nota do mês e o
 * repasse, consulta as anteriores, os dados que vão na nota e a validade do
 * certificado. O que estiver errado vira chamado, pelo Feedback (0108), já
 * com a nota no texto: quem corrige é a cooperativa.
 */
export function NotaFiscalAba() {
  const [doMes, setDoMes] = useState<NotaDoMes | null>(null);
  const [anteriores, setAnteriores] = useState<NotaAnterior[]>([]);
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [chamado, setChamado] = useState<{ tipo: Tipo; mensagem: string; endereco: string } | null>(null);
  const [captura, setCaptura] = useState<EstadoDaCaptura>({ tipo: 'removida' });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [n, h, f] = await Promise.all([
        // Sem competência: o servidor escolhe a mais recente do cooperado.
        ler<NotaDoMes | null>('/api/cooperado/nfse/nota'),
        ler<NotaAnterior[]>('/api/cooperado/nfse/notas'),
        ler<Ficha>('/api/cooperado/nfse/ficha'),
      ]);
      setDoMes(n); setAnteriores(h.filter((x) => x.competencia !== n?.competencia)); setFicha(f);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível ler a nota fiscal');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  /** Abre o Feedback com a nota no texto; a captura é da tela, como no botão do topo. */
  const abrirChamado = async (sobre: 'nota' | 'dados') => {
    let blob: Blob | null = null;
    try { blob = await capturarTela(); } catch { blob = null; }
    setCaptura(blob ? { tipo: 'pronta', blob } : { tipo: 'falhou' });
    const n = doMes?.nota;
    const mensagem = sobre === 'dados'
      ? 'Sobre os meus dados na nota fiscal: '
      : n?.numeroNfse
        ? `Sobre a NFS-e ${n.numeroNfse} de ${doMes!.competencia} (${moeda(doMes!.valor)}): `
        : doMes ? `Sobre a nota de ${doMes.competencia} (${moeda(doMes.valor)}): ` : 'Sobre a minha nota fiscal: ';
    const comp = doMes?.competencia ?? '';
    setChamado({ tipo: 'DUVIDA', mensagem, endereco: `/portal/nota-fiscal?competencia=${comp}${n ? `&nota=${n.idNota}` : ''}` });
  };

  const cert = ficha?.certificado ?? null;

  return (
    <div>
      <div className="co-cabecalho">
        <div>
          <h1 className="co-titulo-tela">Nota fiscal</h1>
          <p className="co-apoio">
            A nota do serviço que você presta à cooperativa. A cooperativa emite com o seu certificado; aqui você acompanha.
          </p>
        </div>
      </div>

      {carregando && !ficha ? <Carregando rotulo="Lendo a nota fiscal" /> : erro ? (
        <p className="co-erro" role="alert">
          {erro}{' '}
          <button type="button" className="co-btn co-btn-texto" onClick={carregar}>Tentar de novo</button>
        </p>
      ) : (
        <div className="co-pilha">
          <section className="co-cartao" aria-labelledby="nf-mes">
            <h2 id="nf-mes" className="co-titulo-cartao">{doMes ? `Nota de ${doMes.competencia}` : 'Nota do mês'}</h2>
            {!doMes ? (
              <p className="co-nota">O mês em andamento não pede nota fiscal.</p>
            ) : (
              <>
                <p className="co-apoio">
                  <Selo papel={doMes.nota?.teste && doMes.situacao === 'autorizada' ? 'info' : PAPEL[doMes.situacao] ?? 'neutro'}>
                    {doMes.situacaoRotulo}{doMes.nota?.teste ? ' (teste)' : ''}
                  </Selo>
                </p>
                <dl className="co-pares">
                  <dt>Valor do serviço</dt><dd>{moeda(doMes.valor)}</dd>
                  {doMes.nota?.numeroNfse && (<><dt>Número</dt><dd>NFS-e {doMes.nota.numeroNfse}</dd></>)}
                  {doMes.nota?.autorizadaEm && (<><dt>Autorizada em</dt><dd>{dataDoDia(doMes.nota.autorizadaEm)}</dd></>)}
                  {doMes.liberadoSemNota && (<><dt>Repasse</dt><dd>Liberado sem nota em {dataDoDia(doMes.liberadoSemNota.em)}</dd></>)}
                </dl>
                {doMes.prontidao.length > 0 && !doMes.liberadoSemNota && (
                  <div className="co-nota co-nota-atencao">
                    <p className="co-rotulo">Antes da nota sair</p>
                    {doMes.prontidao.map((p) => <p key={p.codigo}>{p.frase}.</p>)}
                    <p>Sem a nota, o repasse deste mês espera. Fale com a cooperativa, ou abra um chamado.</p>
                  </div>
                )}
                <div className="co-acoes">
                  <button type="button" className="co-btn co-btn-secundario" onClick={() => abrirChamado('nota')}>
                    Abrir chamado sobre esta nota
                  </button>
                </div>
              </>
            )}
          </section>

          <div className="co-grade-2">
            <section className="co-cartao" aria-labelledby="nf-anteriores">
              <h2 id="nf-anteriores" className="co-titulo-cartao">Notas anteriores</h2>
              {anteriores.length === 0 ? (
                <EstadoVazio titulo="Nenhuma nota anterior">As notas emitidas pela cooperativa aparecem aqui.</EstadoVazio>
              ) : (
                <dl className="co-pares">
                  {anteriores.map((a) => (
                    <Fragment key={a.idNota}>
                      <dt>{a.competencia}</dt>
                      <dd>{a.situacaoRotulo}{a.teste ? ' (teste)' : ''} · {moeda(a.valor)}{a.numeroNfse ? ` · NFS-e ${a.numeroNfse}` : ''}</dd>
                    </Fragment>
                  ))}
                </dl>
              )}
            </section>

            <section className="co-cartao" aria-labelledby="nf-dados">
              <h2 id="nf-dados" className="co-titulo-cartao">Seus dados na nota</h2>
              {ficha && (
                <dl className="co-pares">
                  <dt>Razão social</dt><dd>{ficha.razaoSocial ?? '—'}</dd>
                  <dt>CNPJ</dt><dd>{cnpjFormatado(ficha.cnpj)}</dd>
                  <dt>Inscrição municipal</dt><dd>{ficha.inscricaoMunicipal ?? '—'}</dd>
                  <dt>Regime</dt><dd>{ficha.regime ? REGIME[ficha.regime] : '—'}</dd>
                  <dt>Certificado A1</dt>
                  <dd>
                    {!cert ? <Selo papel="atencao">Não entregue à cooperativa</Selo>
                      : cert.situacao === 'vencido' ? <Selo papel="perigo">Venceu em {dataDoDia(cert.validoAte)}</Selo>
                        : cert.venceEmDias <= 30
                          ? <>Vale até {dataDoDia(cert.validoAte)} <Selo papel="atencao">vence em {cert.venceEmDias} dias</Selo></>
                          : `Vale até ${dataDoDia(cert.validoAte)}`}
                  </dd>
                </dl>
              )}
              <p className="co-apoio">
                Algum dado errado? Quem corrige é a cooperativa.{' '}
                <button type="button" className="co-btn co-btn-texto" onClick={() => abrirChamado('dados')}>Abrir um chamado</button>
              </p>
            </section>
          </div>
        </div>
      )}

      <GavetaFeedback
        aberta={chamado !== null}
        onFechar={() => setChamado(null)}
        captura={captura}
        onCaptura={setCaptura}
        tela="Portal › Nota fiscal"
        onLeu={() => undefined}
        rascunho={chamado ?? undefined}
      />
    </div>
  );
}
